import { memo } from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
    VStack,
    type Column,
    Text,
    TableRender,
    HStack,
    FormField,
} from '@library';
import type {
    SelectionRowItem,
    SelectionTable as SelectionTableProps,
} from '../../../types/customStoryblokComponents';
import { useSelectionColumn } from './useSelectionColumn';

interface StoryblokRowItem extends SelectionRowItem {
    id: string;
}

const SelectionTable = memo(
    ({
        content,
        onChange,
        value,
    }: {
        content: SelectionTableProps;
        onChange: (value: string[] | string) => void;
        value: string[] | string;
    }) => {
        const columns: Column<StoryblokRowItem>[] = [
            {
                key: 'left',
                header: (
                    <VStack>
                        <Text>{content.title}</Text>
                        <Text secondary>{content.subtitle}</Text>
                    </VStack>
                ),
                render(item) {
                    return <Text>{item.label}</Text>;
                },
            },
            {
                key: 'right',
                header: (
                    <VStack align="end">
                        <Text>{content.right_title}</Text>
                        <Text secondary>{content.right_subtitle}</Text>
                    </VStack>
                ),

                render(item) {
                    return (
                        <HStack justify="end">
                            <Text>{item.right_label}</Text>{' '}
                        </HStack>
                    );
                },
            },
        ];

        const selectionColumn = useSelectionColumn<StoryblokRowItem>({
            selectionType: content.selection_type,
            value,
            onChange,
            namePrefix: content.name,
        });

        if (selectionColumn) {
            columns.unshift(selectionColumn);
        }

        const items = content.items.map(item => {
            return { id: item.name, ...item };
        });

        return <TableRender items={items} columns={columns} compact />;
    }
);

const FormSelectionTable = memo((props: SelectionTableProps) => {
    return (
        <Controller
            name={props.name || props._uid}
            render={({
                field: { onChange, value, ref },
                fieldState: { error },
            }) => (
                <FormField
                    label={props.label}
                    error={
                        error?.message ? (
                            <FormattedMessage id={error.message} />
                        ) : undefined
                    }
                >
                    <div tabIndex={0} ref={ref}>
                        <SelectionTable
                            content={props}
                            onChange={onChange}
                            value={value}
                        />
                    </div>
                </FormField>
            )}
            rules={{
                required: props.required ? 'this field is required' : false,
            }}
        />
    );
});

export default memo(FormSelectionTable);
